/**
 * Performance Optimization Utilities
 * Provides optimization for React Flow rendering, data processing, and component re-renders
 */

import { useMemo, useCallback, useRef, useEffect, useState } from 'react';

/**
 * Debounce function to limit function calls
 */
export const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

/**
 * Throttle function to limit function calls to once per interval
 */
export const throttle = (func, limit) => {
  let inThrottle;
  return (...args) => {
    if (!inThrottle) {
      func.apply(null, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

/**
 * Memoized data processor for large datasets
 */
export const useMemoizedDataProcessor = (data, processor, dependencies = []) => {
  return useMemo(() => {
    if (!data || !processor) return null;
    
    const startTime = performance.now();
    const result = processor(data);
    const endTime = performance.now();
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`[Performance] Data processing took ${endTime - startTime} milliseconds`);
    }
    
    return result;
  }, [data, ...dependencies]);
};

/**
 * Optimized React Flow node processing
 */
export const optimizeReactFlowData = (nodes, edges, options = {}) => {
  const {
    maxNodes = 100,
    maxEdges = 200,
    enableClustering = true,
    clusterThreshold = 50
  } = options;

  let optimizedNodes = [...nodes];
  let optimizedEdges = [...edges];

  // Limit nodes if too many
  if (optimizedNodes.length > maxNodes) {
    console.warn(`[Performance] Too many nodes (${optimizedNodes.length}), limiting to ${maxNodes}`);
    optimizedNodes = optimizedNodes.slice(0, maxNodes);
  }

  // Limit edges if too many
  if (optimizedEdges.length > maxEdges) {
    console.warn(`[Performance] Too many edges (${optimizedEdges.length}), limiting to ${maxEdges}`);
    optimizedEdges = optimizedEdges.slice(0, maxEdges);
  }

  // Disable clustering to prevent position-related errors
  // Clustering was causing "Cannot read properties of undefined (reading 'x')" errors

  // Skip position optimization to prevent React Flow errors
  // The position rounding was causing "Cannot read properties of undefined (reading 'x')" errors

  return {
    nodes: optimizedNodes,
    edges: optimizedEdges,
    optimizationApplied: {
      nodeLimit: nodes.length > maxNodes,
      edgeLimit: edges.length > maxEdges,
      clustering: enableClustering && nodes.length > clusterThreshold
    }
  };
};

/**
 * Simple node clustering for performance
 */
const clusterNodes = (nodes, edges) => {
  const clusters = new Map();
  const clusterSize = 5; // Nodes per cluster
  
  // Group nodes by type
  nodes.forEach(node => {
    const type = node.type || 'default';
    if (!clusters.has(type)) {
      clusters.set(type, []);
    }
    clusters.get(type).push(node);
  });

  const clusteredNodes = [];
  const clusteredEdges = [...edges];
  let clusterIndex = 0;

  clusters.forEach((typeNodes, type) => {
    if (typeNodes.length <= clusterSize) {
      // Don't cluster if group is small
      clusteredNodes.push(...typeNodes);
    } else {
      // Create clusters
      for (let i = 0; i < typeNodes.length; i += clusterSize) {
        const clusterNodes = typeNodes.slice(i, i + clusterSize);
        const clusterId = `cluster-${type}-${clusterIndex++}`;
        
        // Create cluster node
        const clusterNode = {
          id: clusterId,
          type: 'cluster',
          position: calculateClusterPosition(clusterNodes),
          data: {
            label: `${type} (${clusterNodes.length})`,
            type: type,
            nodeCount: clusterNodes.length,
            clusteredNodes: clusterNodes
          }
        };
        
        clusteredNodes.push(clusterNode);
      }
    }
  });

  return { nodes: clusteredNodes, edges: clusteredEdges };
};

/**
 * Calculate cluster position based on contained nodes
 */
const calculateClusterPosition = (nodes) => {
  const avgX = nodes.reduce((sum, node) => sum + node.position.x, 0) / nodes.length;
  const avgY = nodes.reduce((sum, node) => sum + node.position.y, 0) / nodes.length;
  
  return { x: Math.round(avgX), y: Math.round(avgY) };
};

/**
 * Optimized scenario data processing
 */
export const optimizeScenarioData = (scenarios, options = {}) => {
  const {
    maxScenarios = 50,
    maxStepsPerScenario = 20,
    maxIndicatorsPerStep = 10,
    maxToolsPerStep = 10
  } = options;

  if (!scenarios || !Array.isArray(scenarios)) {
    return [];
  }

  let optimizedScenarios = [...scenarios];

  // Limit number of scenarios
  if (optimizedScenarios.length > maxScenarios) {
    console.warn(`[Performance] Too many scenarios (${optimizedScenarios.length}), limiting to ${maxScenarios}`);
    optimizedScenarios = optimizedScenarios.slice(0, maxScenarios);
  }

  // Optimize each scenario
  optimizedScenarios = optimizedScenarios.map(scenario => {
    const optimizedScenario = { ...scenario };

    // Limit operational steps
    if (optimizedScenario.operationalSteps && optimizedScenario.operationalSteps.length > maxStepsPerScenario) {
      console.warn(`[Performance] Too many steps in scenario ${scenario.name}, limiting to ${maxStepsPerScenario}`);
      optimizedScenario.operationalSteps = optimizedScenario.operationalSteps.slice(0, maxStepsPerScenario);
    }

    // Optimize each step
    if (optimizedScenario.operationalSteps) {
      optimizedScenario.operationalSteps = optimizedScenario.operationalSteps.map(step => {
        const optimizedStep = { ...step };

        // Limit indicators
        if (optimizedStep.indicators && optimizedStep.indicators.length > maxIndicatorsPerStep) {
          optimizedStep.indicators = optimizedStep.indicators.slice(0, maxIndicatorsPerStep);
        }

        // Limit tools
        if (optimizedStep.tools && optimizedStep.tools.length > maxToolsPerStep) {
          optimizedStep.tools = optimizedStep.tools.slice(0, maxToolsPerStep);
        }

        return optimizedStep;
      });
    }

    return optimizedScenario;
  });

  return optimizedScenarios;
};

/**
 * Performance monitoring hook
 */
export const usePerformanceMonitor = (componentName) => {
  const renderCount = useRef(0);
  const lastRenderTime = useRef(performance.now());

  useEffect(() => {
    renderCount.current += 1;
    const currentTime = performance.now();
    const timeSinceLastRender = currentTime - lastRenderTime.current;
    lastRenderTime.current = currentTime;

    if (process.env.NODE_ENV === 'development') {
      console.log(`[Performance] ${componentName} render #${renderCount.current}, time since last: ${timeSinceLastRender.toFixed(2)}ms`);
    }
  });

  return {
    renderCount: renderCount.current,
    logPerformance: useCallback((operation, duration) => {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[Performance] ${componentName} - ${operation}: ${duration.toFixed(2)}ms`);
      }
    }, [componentName])
  };
};

/**
 * Optimized search function with debouncing
 */
export const useOptimizedSearch = (data, searchFields, delay = 300) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredData, setFilteredData] = useState(data);

  const debouncedSearch = useCallback(
    debounce((term) => {
      if (!term.trim()) {
        setFilteredData(data);
        return;
      }

      const startTime = performance.now();
      const filtered = data.filter(item => {
        return searchFields.some(field => {
          const value = getNestedValue(item, field);
          return value && value.toString().toLowerCase().includes(term.toLowerCase());
        });
      });
      const endTime = performance.now();

      if (process.env.NODE_ENV === 'development') {
        console.log(`[Performance] Search took ${endTime - startTime} milliseconds`);
      }

      setFilteredData(filtered);
    }, delay),
    [data, searchFields, delay]
  );

  useEffect(() => {
    debouncedSearch(searchTerm);
  }, [searchTerm, debouncedSearch]);

  return {
    searchTerm,
    setSearchTerm,
    filteredData
  };
};

/**
 * Get nested object value by path
 */
const getNestedValue = (obj, path) => {
  return path.split('.').reduce((current, key) => current?.[key], obj);
};

/**
 * Virtualization helper for large lists
 */
export const useVirtualization = (items, itemHeight, containerHeight) => {
  const [scrollTop, setScrollTop] = useState(0);

  const visibleItems = useMemo(() => {
    const startIndex = Math.floor(scrollTop / itemHeight);
    const endIndex = Math.min(
      startIndex + Math.ceil(containerHeight / itemHeight) + 1,
      items.length
    );

    return {
      startIndex,
      endIndex,
      items: items.slice(startIndex, endIndex),
      totalHeight: items.length * itemHeight,
      offsetY: startIndex * itemHeight
    };
  }, [items, itemHeight, containerHeight, scrollTop]);

  const handleScroll = useCallback((e) => {
    setScrollTop(e.target.scrollTop);
  }, []);

  return {
    visibleItems,
    handleScroll
  };
};

/**
 * Memory usage monitoring
 */
export const useMemoryMonitor = (componentName) => {
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && 'memory' in performance) {
      const logMemory = () => {
        const memory = performance.memory;
        console.log(`[Memory] ${componentName}:`, {
          used: `${(memory.usedJSHeapSize / 1048576).toFixed(2)} MB`,
          total: `${(memory.totalJSHeapSize / 1048576).toFixed(2)} MB`,
          limit: `${(memory.jsHeapSizeLimit / 1048576).toFixed(2)} MB`
        });
      };

      logMemory();
      const interval = setInterval(logMemory, 10000); // Log every 10 seconds

      return () => clearInterval(interval);
    }
  }, [componentName]);
};

/**
 * Optimized event handlers
 */
export const useOptimizedHandlers = () => {
  const handlersRef = useRef({});

  const createOptimizedHandler = useCallback((key, handler, dependencies = []) => {
    // Check if handler needs to be updated
    const needsUpdate = !handlersRef.current[key] ||
      dependencies.some((dep, index) => dep !== handlersRef.current[key].deps[index]);

    if (needsUpdate) {
      handlersRef.current[key] = {
        handler: (...args) => handler(...args),
        deps: dependencies
      };
    }
    return handlersRef.current[key].handler;
  }, []);

  return { createOptimizedHandler };
};

export default {
  debounce,
  throttle,
  useMemoizedDataProcessor,
  optimizeReactFlowData,
  optimizeScenarioData,
  usePerformanceMonitor,
  useOptimizedSearch,
  useVirtualization,
  useMemoryMonitor,
  useOptimizedHandlers
};
